import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Download, Trash2, Loader2, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPdf, formatErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MyBookings from "@/components/dashboard/MyBookings";

export default function Dashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState(null);

  useEffect(() => { if (user) api.get("/quotes/mine").then((r) => setQuotes(r.data)).catch((e) => toast.error(formatErr(e))); }, [user]);

  if (user === null) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>;
  if (user === false) return <Navigate to="/login?next=/dashboard" replace />;

  const remove = async (id) => {
    try { await api.delete(`/quotes/${id}`); setQuotes(quotes.filter((q) => q.id !== id)); toast.success("Quotation removed"); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-brand-accent">My Quotations</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-brand-ink mt-3">Hello, {user.name.split(" ")[0]}.</h1>
        </div>
        <Link to="/estimate" className="inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-brand-accent text-brand-cream px-6 py-3 text-sm transition-colors" data-testid="dashboard-new-quote-btn"><Plus className="w-4 h-4" /> New estimate</Link>
      </div>
      {!quotes ? (
        <Loader2 className="w-6 h-6 animate-spin text-brand-accent mt-16" />
      ) : quotes.length === 0 ? (
        <div className="mt-14 rounded-2xl border border-dashed border-brand-line p-16 text-center" data-testid="dashboard-empty">
          <FileText className="w-8 h-8 text-brand-accent mx-auto" strokeWidth={1.5} />
          <p className="font-serif-display text-3xl text-brand-ink mt-4">No quotations yet</p>
          <p className="text-sm text-brand-muted mt-2">Upload a floor plan to get your first estimate in under a minute.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {quotes.map((q) => (
            <div key={q.id} className="rounded-2xl border border-brand-line bg-brand-cream p-7 hover:-translate-y-1 transition-transform duration-300" data-testid={`dashboard-quote-${q.id}`}>
              <div className="flex justify-between items-start">
                <p className="font-mono-num text-xs text-brand-accent">{q.quote_number}</p>
                <span className="text-[10px] uppercase tracking-[0.15em] bg-brand-sand text-brand-muted px-3 py-1 rounded-full">{q.status}</span>
              </div>
              <Link to={`/quote/${q.id}`} className="block mt-5" data-testid={`dashboard-quote-link-${q.id}`}>
                <p className="font-serif-display text-3xl text-brand-ink hover:text-brand-accent transition-colors">{q.tier_info.label} · {q.total_area} sq.ft</p>
              </Link>
              <p className="text-sm text-brand-muted mt-1">{q.rooms.length} rooms · {new Date(q.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</p>
              <p className="font-serif-display text-4xl text-brand-ink mt-6">{inr(q.total)}</p>
              <div className="flex gap-2 mt-6">
                <button onClick={() => downloadPdf(q.id, q.quote_number)} className="flex-1 inline-flex justify-center items-center gap-2 rounded-full border border-brand-line hover:border-brand-accent py-2.5 text-sm" data-testid={`dashboard-pdf-${q.id}`}><Download className="w-4 h-4" /> PDF</button>
                <button onClick={() => remove(q.id)} className="px-4 rounded-full border border-brand-line hover:border-red-300 text-brand-muted-light hover:text-red-700" data-testid={`dashboard-delete-${q.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <MyBookings />
    </section>
  );
}
